import { useMemo } from 'react'
import type { ConnectedSummaryLine } from '../../../../../components/ConnectedSummaryLines'
import { ConnectedSummaryLinesV2 } from '../../../../../components/ConnectedSummaryLinesV2'
import { useBottomSheet } from '../../../../../../../state-management/contexts/BottomSheetContext'
import { EventNames } from '../../../../../../../types/misc.types'
import { convertEpochToDate } from '../../../../../../../utils/utils'
import { lightImpactVibrate, postCTA, trackEvent } from '../../../../../../../utils/appBridge'
import {
  isSipPlansFetchFailed,
  isSipPlansHeaderLoading,
  useSipPlansContext
} from '../../SipPlansProvider'
import { PlansScreen, PlansSipListEventIds } from '../../../../analytics/plansEvents'
import { HeaderHeroDescription } from '../../../../lumpsum/plans-list/lumpsum-ui-components/HeaderHeroDescription'
import { SipHeaderHeroSkeleton } from '../../sip-ui-components/SipHeaderHeroSkeleton'
import { HeaderRiveBackdrop } from '../../../../lumpsum/plans-list/header/common/HeaderRiveBackdrop'
import {
  getPlansFlowTheme,
  PlansThemeFlow,
  resolveHeaderAppearanceWithRiskProfile,
  type LumpsumHeaderAppearance
} from '../../../../lumpsum/plans-list/header/common/riskProfileHeaderTheme'
import type { AllocationRiskProfile } from '../../../../../expert-review/types/allocation.types'
import { SipPlanMode } from '../../../api/types'
import type { SipPlansOverviewResponse } from '../../../api/overview.types'
import type { DerivedYourPlanCategory } from '../../deriveSipPlansView'
import { isSipYourPlanListFullyEmpty } from '../../../../utils/sip/planListUtils'
import {
  ListPlansPlanUpdatedBanner,
  ListPlansPlanUpdatedBannerType,
  shouldShowPlanUpdatedBanner
} from '../../../../lumpsum/plans-list/header/common/ListPlansPlanUpdatedBanner'
import { SipYourPlanInfoBottomSheet } from './SipYourPlanInfoBottomSheet'

const SIP_YOUR_PLAN_HEADER_DESCRIPTION =
  'This plan optimises your SIPs to keep your portfolio aligned with your allocation strategy.'
const SIP_YOUR_PLAN_TOP_SHAPE_STATUS_COPY =
  'No changes are needed this month. We’ll review it again next month to make sure it stays that way.'

export function SipYourPlanHeader() {
  const { planMode, plansOverview, derivedPlans, plansQuery } = useSipPlansContext()
  const { openBottomSheet, closeBottomSheet } = useBottomSheet()
  const headerAppearance = resolveSipYourPlanHeaderAppearance(plansOverview?.riskProfile)
  const isHeaderActive = planMode === SipPlanMode.YOUR_PLAN
  const isHeaderLoading = isSipPlansHeaderLoading(
    SipPlanMode.YOUR_PLAN,
    plansOverview != null,
    plansQuery
  )
  const isPlansEmpty =
    !isHeaderLoading && isSipYourPlanListFullyEmpty(derivedPlans?.yourPlanCategories)
  const isPlansFetchFailed = isSipPlansFetchFailed(SipPlanMode.YOUR_PLAN, plansQuery)
  const plansListHero = useMemo(
    () => getSipYourPlanHeroFields(plansOverview, derivedPlans?.yourPlanCategories),
    [plansOverview, derivedPlans?.yourPlanCategories]
  )

  if (isPlansEmpty || isPlansFetchFailed) {
    return null
  }

  const connectedSummaryLines = isHeaderLoading
    ? []
    : getSipYourPlanConnectedSummaryLines(plansListHero)
  const showPlanUpdatedBanner =
    !isHeaderLoading &&
    shouldShowPlanUpdatedBanner(plansOverview?.planCount, plansOverview?.planSnapshotDate)

  function handleStrategyLandingCtaClick() {
    const strategyLandingCta = plansOverview?.strategyLandingCta
    if (strategyLandingCta == null) return
    lightImpactVibrate()
    trackEvent({
      eventName: EventNames.UserClicked,
      payload: {
        ScreenName: PlansScreen.SipYourPlan,
        eventId: PlansSipListEventIds.StrategyLandingCta,
        buttonLabel: 'Strategy landing'
      }
    })
    postCTA(JSON.stringify(strategyLandingCta))
  }

  function handleSipPlanInfoClick() {
    lightImpactVibrate()
    trackEvent({
      eventName: EventNames.UserClicked,
      payload: {
        ScreenName: PlansScreen.SipYourPlan,
        eventId: PlansSipListEventIds.PlanInfo,
        buttonLabel: 'Plan info'
      }
    })
    openBottomSheet(<SipYourPlanInfoBottomSheet onDismiss={closeBottomSheet} />)
  }

  return (
    <div>
      {isHeaderLoading ? (
        <SipHeaderHeroSkeleton backgroundColor={headerAppearance.backgroundColor} isIdeal={false} />
      ) : (
        <>
          {showPlanUpdatedBanner ? (
            <ListPlansPlanUpdatedBanner
              bannerType={ListPlansPlanUpdatedBannerType.SIP}
              planSnapshotDate={plansOverview?.planSnapshotDate ?? 0}
              planDurationInMonths={plansOverview?.planDurationInMonths}
            />
          ) : null}
          <div
            className={`relative overflow-visible px-5${plansListHero.isTopShape && plansOverview?.isPlanSubscriptionEligible !== true ? ' pb-8' : ' pb-1'}${showPlanUpdatedBanner ? '' : ' pt-6'}`}
          >
            <HeaderRiveBackdrop
              isActive={isHeaderActive}
              riveSrc={headerAppearance.riveSrc}
              headerSvgSrc={headerAppearance.headerSvgSrc}
              backgroundColor={headerAppearance.backgroundColor}
              onLandingCtaClick={
                plansOverview?.strategyLandingCta == null
                  ? undefined
                  : handleStrategyLandingCtaClick
              }
            />

            <div className="relative z-[1] flex gap-3 pointer-events-none">
              <div className="min-w-0 flex-1 max-w-[calc(100%-130px)] pointer-events-auto">
                {plansListHero.reviewedOnLabel.length > 0 ? (
                  <div className={headerAppearance.reviewedOnClassName}>
                    {plansListHero.reviewedOnLabel}
                  </div>
                ) : null}

                <div className="pt-1 text-[22px] font-[500] leading-[130%] text-[#232323]">
                  {plansListHero.headline}
                </div>

                {connectedSummaryLines.length > 0 ? (
                  <div className="pt-3 pl-[2px]">
                    <ConnectedSummaryLinesV2
                      lines={connectedSummaryLines}
                      connectorColor={headerAppearance.connectorColor}
                      connectorThickness={2}
                      gap={4}
                    />
                  </div>
                ) : null}

                {plansListHero.isTopShape ? (
                  <p className="pt-3 text-[13px] font-[400] leading-[150%] text-[#6A6E6E]">
                    {SIP_YOUR_PLAN_TOP_SHAPE_STATUS_COPY}
                  </p>
                ) : null}
              </div>
            </div>

            <HeaderHeroDescription
              description={SIP_YOUR_PLAN_HEADER_DESCRIPTION}
              onLearnMoreClick={handleSipPlanInfoClick}
              className="relative z-[1] w-full max-w-[300px]"
            />
          </div>
        </>
      )}
    </div>
  )
}

type SipYourPlanHeroFields = {
  reviewedOnLabel: string
  headline: string
  isTopShape: boolean
  stopSipCount: number
  startSipCount: number
  decreaseSipCount: number
  increaseSipCount: number
}

function getSipYourPlanHeroFields(
  plansOverview: SipPlansOverviewResponse | undefined,
  yourPlanCategories: DerivedYourPlanCategory[] | undefined
): SipYourPlanHeroFields {
  const stopSipCount = countYourPlanBucketItems(yourPlanCategories, 'stopRecommendations')
  const startSipCount = countYourPlanBucketItems(yourPlanCategories, 'startRecommendations')
  const decreaseSipCount = countYourPlanBucketItems(yourPlanCategories, 'decreaseRecommendations')
  const increaseSipCount = countYourPlanBucketItems(yourPlanCategories, 'increaseRecommendations')
  const isTopShape = stopSipCount + startSipCount + decreaseSipCount + increaseSipCount === 0

  return {
    reviewedOnLabel: formatSipYourPlanReviewedOnLabel(plansOverview?.planSnapshotDate),
    headline: isTopShape ? 'All your SIPs are in top shape' : 'SIP Plan',
    isTopShape,
    stopSipCount,
    startSipCount,
    decreaseSipCount,
    increaseSipCount
  }
}

/** Fixed order: stop → start → increase → decrease; omit zero counts. */
function getSipYourPlanConnectedSummaryLines(
  plansListHero: SipYourPlanHeroFields
): ConnectedSummaryLine[] {
  if (plansListHero.isTopShape) {
    return []
  }

  const lines: ConnectedSummaryLine[] = []

  if (plansListHero.stopSipCount > 0) {
    lines.push({
      html: `<span class="font-[500] text-[#3d3d3d]">${formatSipCountLabel(plansListHero.stopSipCount)}</span> to stop`
    })
  }

  if (plansListHero.startSipCount > 0) {
    lines.push({
      html: `<span class="font-[500] text-[#3d3d3d]">${formatSipCountLabel(plansListHero.startSipCount)}</span> to start`
    })
  }

  if (plansListHero.increaseSipCount > 0) {
    lines.push({
      html: `<span class="font-[500] text-[#3d3d3d]">${formatSipCountLabel(plansListHero.increaseSipCount)}</span> to increase`
    })
  }

  if (plansListHero.decreaseSipCount > 0) {
    lines.push({
      html: `<span class="font-[500] text-[#3d3d3d]">${formatSipCountLabel(plansListHero.decreaseSipCount)}</span> to decrease`
    })
  }

  return lines
}

function countYourPlanBucketItems(
  yourPlanCategories: DerivedYourPlanCategory[] | undefined,
  bucketKey:
    | 'stopRecommendations'
    | 'startRecommendations'
    | 'decreaseRecommendations'
    | 'increaseRecommendations'
): number {
  return (yourPlanCategories ?? []).reduce((itemTotal, category) => {
    return itemTotal + (category?.[bucketKey]?.length ?? 0)
  }, 0)
}

function formatSipCountLabel(sipCount: number): string {
  return `${sipCount} SIP${sipCount === 1 ? '' : 's'}`
}

function formatSipYourPlanReviewedOnLabel(planSnapshotDate?: number | null): string {
  if (!planSnapshotDate) {
    return ''
  }
  return `Reviewed on ${convertEpochToDate(planSnapshotDate, '%_d %b %Y')}`
}

const SIP_YOUR_PLAN_FLOW_THEME = getPlansFlowTheme(PlansThemeFlow.SIP_YOUR_PLAN)
const SIP_YOUR_PLAN_HEADER_STYLE = {
  backgroundColor: SIP_YOUR_PLAN_FLOW_THEME.topColor,
  chromeBackground: SIP_YOUR_PLAN_FLOW_THEME.chromeBackground,
  reviewedOnClassName: SIP_YOUR_PLAN_FLOW_THEME.periodLabelClassName,
  connectorColor: SIP_YOUR_PLAN_FLOW_THEME.connectorColor
}

export function resolveSipYourPlanHeaderAppearance(
  riskProfile?: AllocationRiskProfile | null
): LumpsumHeaderAppearance {
  return resolveHeaderAppearanceWithRiskProfile({
    riskProfile,
    strategyPalette: 'allocation',
    ...SIP_YOUR_PLAN_HEADER_STYLE
  })
}
